import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Dashboard from './pages/Dashboard';
import Devices from './pages/Devices';
import ThreatDetection from './pages/ThreatDetection';
import ThreatIntelligence from './pages/ThreatIntelligence';
import AccessControl from './pages/AccessControl';
import CloudSecurity from './pages/CloudSecurity';
import Reports from './pages/Reports';
import AuditLogs from './pages/AuditLogs';
import TeamManagement from './pages/TeamManagement';
import UserSettings from './pages/UserSettings';
import Pricing from './pages/Pricing';
import CheckoutPage from './pages/CheckoutPage';
import CheckoutSuccess from './pages/CheckoutSuccess';
import AcceptInvitation from './pages/AcceptInvitation';
import TrialExpired from './pages/TrialExpired';

const ProtectedRoute = ({ children }) => {
  const { token } = useAuth();
  return token ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { token } = useAuth();
  return token ? <Navigate to="/dashboard" /> : children;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <LandingPage />
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicRoute>
                <Signup />
              </PublicRoute>
            }
          />
          <Route
            path="/pricing"
            element={
              <PublicRoute>
                <Pricing />
              </PublicRoute>
            }
          />

          {/* Password Reset Routes - Allow access regardless of auth status */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected Routes with Navbar */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Navbar />
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/devices"
            element={
              <ProtectedRoute>
                <Navbar />
                <Devices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/threat-detection"
            element={
              <ProtectedRoute>
                <Navbar />
                <ThreatDetection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/threat-intelligence"
            element={
              <ProtectedRoute>
                <Navbar />
                <ThreatIntelligence />
              </ProtectedRoute>
            }
          />
          <Route
            path="/access-control"
            element={
              <ProtectedRoute>
                <Navbar />
                <AccessControl />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cloud-security"
            element={
              <ProtectedRoute>
                <Navbar />
                <CloudSecurity />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Navbar />
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute>
                <Navbar />
                <AuditLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute>
                <Navbar />
                <TeamManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Navbar />
                <UserSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accept-invitation"
            element={
              <ProtectedRoute>
                <Navbar />
                <AcceptInvitation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trial-expired"
            element={
              <ProtectedRoute>
                <Navbar />
                <TrialExpired />
              </ProtectedRoute>
            }
          />

          {/* Checkout Routes */}
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;